import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import { usePostPokeToUserSellTractor } from '../../../hooks/api/product/mutations/usePostPokeToUserSellTractor.mutation';
import { usePostVirtualNumberMutation } from '../../../hooks/api/virtual-number/mutations/usePostVirtualNumber.mutation';
import { useAppDispatch } from '../../../hooks/store/useStore';
import { MachineType } from '../../../interfaces/Brand.interfaces';
import { NumberKeyboard } from '../../../pages/OpenMarket/TractorPoking/TractorPokingDetailPage/components/NumberKeyboard';
import { useNotification } from '../../../provider/ToastNotificationProvider';
import { virtualNumberLoadingSlice } from '../../../store/virtualNumberLoadingSlice';
import { formatPrice } from '../../../utils/formatter/formatPrice.util';
import { openSmsWithPokeInfo } from '../../../utils/opener/openSmsWithPokeInfo.util';
import { PopupConfirmModal } from './popup/PopupConfirmModal';

interface IPriceSuggestModalProps {
  isShow: boolean;
  onClose: () => void;
  productId: string;
  productCode: string;
  hopeSellingPrice: number;
  productTitle: string;
  brandName: string;
  modelName: string;
  phoneNumber: string;
  machineType: MachineType;
}

export const PriceSuggestModal = ({
  isShow,
  onClose,
  productId,
  productCode,
  hopeSellingPrice,
  productTitle,
  brandName,
  modelName,
  phoneNumber,
  machineType,
}: IPriceSuggestModalProps) => {
  const [suggestPrice, setSuggestPrice] = useState<number>(hopeSellingPrice);

  const dispatch = useAppDispatch();
  const virtualNumberLoadingActions = virtualNumberLoadingSlice.actions;
  const { mutate: postPokeToUserSellTractor } = usePostPokeToUserSellTractor();
  const { mutate: postVirtualNumber } = usePostVirtualNumberMutation();
  const { openNotification } = useNotification();

  const isSuggestPriceOutOfRange = suggestPrice < hopeSellingPrice * 0.6 || suggestPrice > hopeSellingPrice * 1.4;

  const closePriceSuggestModal = () => {
    onClose();
    setSuggestPrice(hopeSellingPrice);
  };

  useEffect(() => {
    if (suggestPrice < 0) {
      setSuggestPrice(0);
    }
  }, [suggestPrice]);

  useEffect(() => {
    if (isShow) {
      setSuggestPrice(hopeSellingPrice);
    }
  }, [isShow, hopeSellingPrice]);

  return (
    <PopupConfirmModal
      isShow={isShow}
      hasCloseButton={false}
      closeModal={closePriceSuggestModal}
      title={'✉️ 문자로 문의하기'}
      description={'가격을 입력하시면 문자 메세지에\n가격을 넣어 작성해드려요!'}
      element={
        <>
          <PriceSuggestInputWrapper>
            <PriceSuggestInput
              placeholder={'제안가 입력 (만원 단위)'}
              hasError={isSuggestPriceOutOfRange}
              value={`${suggestPrice === 0 ? '0' : formatPrice(suggestPrice)}원`}
              onChange={() => {}}
            />
            <PriceSuggestInputErrorMessageWrapper>
              {isSuggestPriceOutOfRange && (
                <PriceSuggestInputErrorMessage>
                  {suggestPrice > hopeSellingPrice
                    ? '금액이 너무 높은것 같아요 🙄'
                    : '너무 낮은 금액으로는 제안할 수 없어요 😥'}
                </PriceSuggestInputErrorMessage>
              )}
            </PriceSuggestInputErrorMessageWrapper>
          </PriceSuggestInputWrapper>
          <PriceAdjustmentControls>
            <PriceAdjustmentButton
              onClick={() => {
                setSuggestPrice(suggestPrice - 500000);
              }}
            >
              <PriceAdjustmentButtonText>{'-50만'}</PriceAdjustmentButtonText>
            </PriceAdjustmentButton>
            <PriceAdjustmentButton
              onClick={() => {
                setSuggestPrice(suggestPrice - 1000000);
              }}
            >
              <PriceAdjustmentButtonText>{'-100만'}</PriceAdjustmentButtonText>
            </PriceAdjustmentButton>
            <PriceAdjustmentButton
              onClick={() => {
                setSuggestPrice(suggestPrice - 10000000);
              }}
            >
              <PriceAdjustmentButtonText>{'-1000만'}</PriceAdjustmentButtonText>
            </PriceAdjustmentButton>
          </PriceAdjustmentControls>
          <NumberKeyboard
            handleKeyPress={(pressedNumber: string) => {
              if (isSuggestPriceOutOfRange && suggestPrice > hopeSellingPrice) return;
              setSuggestPrice(Number(String(suggestPrice / 10000) + String(Number(pressedNumber))) * 10000);
            }}
            handleDelete={() => {
              setSuggestPrice(Number(String(suggestPrice / 10000).slice(0, -1)) * 10000);
            }}
            handleReset={() => setSuggestPrice(hopeSellingPrice)}
          />
        </>
      }
      buttonArray={[
        {
          text: '문자 보내러 가기',
          onClick: () => {
            ampli.pvmClickOdpSuggestPriceModalConfirmButton({
              brand: brandName,
              model: modelName,
              suggest_price: suggestPrice,
              title: productTitle,
            });

            if (isSuggestPriceOutOfRange) {
              return;
            }

            if (!phoneNumber) {
              openNotification({
                message: '판매자 전화번호 정보가 없습니다.',
                type: 'error',
              });
              return;
            }

            if (productId) {
              const isProduction = process.env.REACT_APP_CLIENT_ENVIRONMENT === 'production';

              // 1. 가격 제안 저장
              postPokeToUserSellTractor(
                { tractorId: productId, reqBody: { suggestedPrice: suggestPrice } },
                {
                  onSuccess: () => {
                    // 2. 운영 환경이 아니면 실제 번호로 바로 SMS
                    if (!isProduction) {
                      if (suggestPrice && productId && phoneNumber && machineType) {
                        openSmsWithPokeInfo({
                          suggestedPrice: formatPrice(suggestPrice),
                          productId,
                          productOwnerPhoneNumber: phoneNumber,
                          machineType,
                        });
                        closePriceSuggestModal();
                      }
                      return;
                    }

                    // 3. 운영 환경에서만 안심번호 발급
                    // 전역 로딩 시작
                    dispatch(virtualNumberLoadingActions.setVirtualNumberLoading(true));

                    // 4. 안심번호 발급
                    postVirtualNumber(
                      {
                        productId,
                        productCode,
                        realNumber: phoneNumber,
                      },
                      {
                        onSuccess: (data) => {
                          // 전역 로딩 종료
                          dispatch(virtualNumberLoadingActions.setVirtualNumberLoading(false));

                          // 5. 안심번호로 SMS 앱 열기
                          if (suggestPrice && productId && machineType) {
                            openSmsWithPokeInfo({
                              suggestedPrice: formatPrice(suggestPrice),
                              productId,
                              productOwnerPhoneNumber: data.virtualNumber,
                              machineType,
                            });
                            closePriceSuggestModal();
                          }
                        },
                        onError: (error) => {
                          // 전역 로딩 종료
                          dispatch(virtualNumberLoadingActions.setVirtualNumberLoading(false));

                          openNotification({
                            message: '안심번호 발급에 실패했습니다. 잠시 후 다시 시도해주세요.',
                            type: 'error',
                          });
                          console.error('안심번호 발급 실패:', error);
                        },
                      },
                    );
                  },
                },
              );
            }
          },
          color: 'blue',
        },
        {
          text: '닫기',
          onClick: closePriceSuggestModal,
          color: 'gray',
        },
      ]}
    />
  );
};

const PriceSuggestInputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const PriceSuggestInput = styled.input<{ hasError: boolean }>`
  display: flex;
  height: 48px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  border-radius: var(--radius-large, 16px);
  border: 1px solid
    ${(props) =>
      props.hasError
        ? 'var(--Light-Scheme-Color-primary-red, #FF3049)'
        : 'var(--Light-Scheme-Monochrome-grey20, #ececf3)'};

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  text-overflow: ellipsis;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */

  &::placeholder {
    color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  }
`;

const PriceSuggestInputErrorMessageWrapper = styled.div`
  position: absolute;
  bottom: -3px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const PriceSuggestInputErrorMessage = styled.p`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const PriceAdjustmentControls = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const PriceAdjustmentButton = styled.button`
  display: flex;
  height: 32px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey20, #ececf3);

  cursor: pointer;
`;

const PriceAdjustmentButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 10.2px */
`;
